import { InlineAlert } from '../../components/BaseComponents'
import { useState, useEffect } from "react";
import { Button, LoadingSpinner } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import Input from "../../components/BaseComponents/Forms/Input";
import useApi from "../../hooks/useApi";
import useCleanup from "../../hooks/useCleanup";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type AddTrainingUserModalProps = {
    trainingSessionId: string,
    sessionData?: any,
    onClose: () => void,
    onAdded: () => void,
    isOpen?: boolean,
};

function AddTrainingUserModal({ trainingSessionId, sessionData: parentSessionData, onClose, onAdded, isOpen }: AddTrainingUserModalProps) {
    const [email, setEmail] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sessionData, setSessionData] = useState<any>(parentSessionData);
    const api = useApi();

    const loadSessionData = useCleanup(() => {
        if (!isOpen) {
            return;
        }
        let cancelled = false;
        const run = async () => {
            try {
                const result = await api.get(`admin/training-sessions/${trainingSessionId}`);
                if (cancelled) {
                    return;
                }
                setSessionData(result.data);
            } catch (err) {
                // Silently fail if session data can't be loaded; display fallback title
                if (!cancelled) {
                    console.error('Failed to load session data:', err);
                }
            }
        }
        run();
        return () => {
            cancelled = true;
        }
    })

    useEffect(() => {
        loadSessionData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trainingSessionId, isOpen])

    // Sync with parent sessionData when it changes
    useEffect(() => {
        if (parentSessionData) {
            setSessionData(parentSessionData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentSessionData])

    const reset = () => {
        setEmail('');
        setOrganizationName('');
        setError('');
    };

    const create = async () => {
        setLoading(true);
        setError('');
        try {
            await api.post(`/admin/training-sessions/${trainingSessionId}/users`, {
                email,
                organizationName: organizationName || undefined,
                _csrf: await getCSRFToken(),
            })
            reset();
            onAdded();
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Failed to add this user.'));
        } finally {
            setLoading(false);
        }
    }

    const formatSessionDisplay = () => {
        if (!sessionData?.name) return '';
        let display = sessionData.name;
        if (sessionData.start_date) {
            const date = new Date(sessionData.start_date);
            const formatted = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
            display += ` (starts: ${formatted})`;
        }
        return display;
    }

    return (
        <CanonicalModal
            open={!!isOpen}
            onClose={() => { reset(); onClose(); }}
            size="small"
            title="Add training user"
            subtitle={sessionData?.name ? formatSessionDisplay() : undefined}
            actions={
                <>
                    <Button
                        onClick={create}
                        additionalClass="mr-5"
                        disabled={!email || loading}
                        type="Primary"
                        text="Add user"
                    />
                    <Button onClick={onClose} type="Outlined" text="Cancel" />
                </>
            }
        >
            <Input
                label="Email"
                name="email"
                additionalClass="mt-8"
                value={email}
                onChange={e => setEmail(e.target.value)}
                type="email"
                placeholder="name@example.com..."
            />
            <Input
                label="Organisation name"
                name="organizationName"
                additionalClass="mt-8"
                value={organizationName}
                onChange={e => setOrganizationName(e.target.value)}
                type="text"
                placeholder="Optional (assigns user to a group/org)"
            />
            { loading && <LoadingSpinner /> }
            { error && (
                <InlineAlert type='error' className='mt-4'>{ error }
                </InlineAlert>
            )}
        </CanonicalModal>
    )
}

export default AddTrainingUserModal;
